import { Inject } from '@odinjs/odin';

import { Endpoint, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { ActionAPI, DeckAPI, FetchAPI, FieldAPI, PanelAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { NegotiationContractAttachmentsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { ContractualDraft } from './negotiation-contractual-draft-controller';

@Controller({ fragment: FragmentNames.NEGOTIATION_CONTRACT_ATTACHMENTS })
class NegotiationContractAttachmentsController extends BaseController {

  @Inject(Aliases.ACTION_REQUEST_CHANGE)
  private actionRequestChange: ActionAPI;
  @Inject(Aliases.ACTION_DOWNLOAD)
  private actionDownload: ActionAPI;
  @Inject(Aliases.PANEL_REQUEST_CHANGE)
  private panelRequestChange: PanelAPI;
  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;
  @Inject(Aliases.DECK_ATTACHMENTS)
  private deckAttachments: DeckAPI;

  @OnMount(Aliases.FETCH_ATTACHMENTS)
  onMountFetchClauses(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, attachments: true });
    api.forceFetch();
  }

  @OnMount(Aliases.FIELD_COUNTER_OFFER)
  onMountCounterOffer(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { ieValueCounterProposal } = api.getData() as unknown as ContractualDraft;
    if (ieValueCounterProposal) {
      api.setVisible(ieValueCounterProposal == 'S');
    }
  }

  @OnAfterFetch(Aliases.FETCH_ATTACHMENTS)
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as ContractualDraft[];
    if (data && !isEmptyArrayOrObject(data)) {
      const { edit } = data[0];
      this.actionRequestChange.setEnabled(edit == 'S');
    }
    this.actionDownload.setEnabled(!isEmptyArrayOrObject(data));
  }

  @OnClick(Aliases.ACTION_REQUEST_CHANGE)
  onClickAccept(): void {
    const selectedItens = this.deckAttachments.state.selectedCards;

    if (isEmptyArrayOrObject(selectedItens)) {
      ToastServiceProviderAccreditation.error(MessageService.translate('pa.no-file-selected'));
      return;
    }

    this.panelRequestChange.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_REQUEST_CHANGE)
  onAcceptProposal(event: SubmitEvent): void {
    const { counterOffer } = event.record as ContractualDraft;
    const selectedItens = this.deckAttachments.state.selectedCards;
    const body = { counterOffer, selectedItens };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_CONTRACT_DRAFT, { nrSeqProposal, nrSeqNegotiation, counterOffer }, { body }).then(() => {
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.request-success'));
      this.fetchAttachments.forceFetch();
      this.panelRequestChange.setVisible(false);
    }).catch((error) => {
      this.panelRequestChange.setVisible(false);
      ToastServiceProviderAccreditation.error(error.body);
    });
  }

  @OnClose(Aliases.MODAL_REQUEST_CHANGE)
  onCloseModalAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnCancel(Aliases.FORM_REQUEST_CHANGE)
  onCancelFormAccept(): void {
    this.panelRequestChange.setVisible(false);
  }

  @OnClick(Aliases.ACTION_DOWNLOAD)
  onClickDownload(): void {
    const selectedItens = this.deckAttachments.state.selectedCards;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();

    if (isEmptyArrayOrObject(selectedItens)) {
      ToastServiceProviderAccreditation.error(MessageService.translate('pa.no-file-selected'));
      return;
    }

    selectedItens.forEach(nrSeqItemProposal => {
      HttpService.get<ContractualDraft>(Endpoint.GET.NEGOTIATION_DRAFT, { nrSeqProposal, nrSeqNegotiation, nrSeqItemProposal, 'attachments': true })
        .then((resp) => {
          const dsFile = resp.body?.dsFile;
          FileService.download(Endpoint.GET.NEGOTIATION_DRAFT_ATTACHMENT, dsFile, { nrSeqNegotiation, nrSeqProposal, nrSeqItemProposal },
            HttpMethod.GET)
            .catch(() => ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-finding-file')));
        }).catch(() => ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-finding-file')));
    });

  }

}

export { NegotiationContractAttachmentsController };
